import { EncoderDecoder } from '@iotize/common/converter/api';
import { TLV } from '@iotize/common/tlv';
import { BodyConverter } from '@iotize/tap/client/api';

import { TLVConverter } from '../tlv-converter';

interface VariableMeta {
  id: number;
  converter?: EncoderDecoder<any, Uint8Array>;
  name?: string;
}
export class TlvBundleConverter<DataType = Record<string, Uint8Array>>
  implements BodyConverter<DataType>
{
  bundleId = 0;

  constructor(
    protected variables: VariableMeta[] = [],
    protected tlvConverter: TLVConverter = new TLVConverter(),
  ) {}

  decode(data: Uint8Array | TLV.TlvNode, context?: any): DataType {
    if (data instanceof Uint8Array) {
      // Why this?
      // const buffer = Buffer.from(data.buffer);
      data = this.tlvConverter.decode(data);
    }
    const result = this._convertFromTlvTree(data);
    if (!result || typeof result !== 'object' || result instanceof Uint8Array) {
      throw new Error(`Invalid TLV frame`); // TODO proper error code
    }
    return result as DataType;
  }

  // {
  //     "header": {
  //         "id": 1,
  //         "type": 2,
  //         "len": 12
  //     },
  //     "children": [
  //         {
  //             "header": {
  //                 "id": 1,
  //                 "type": 3,
  //                 "len": 4
  //             },
  //             "payload": {
  //                 "type": "Buffer",
  //                 "data": [
  //                     64,
  //                     83,
  //                     127,
  //                     58
  //                 ]
  //             }
  //         },
  //         {
  //             "header": {
  //                 "id": 2,
  //                 "type": 3,
  //                 "len": 4
  //             },
  //             "payload": {
  //                 "type": "Buffer",
  //                 "data": [
  //                     65,
  //                     243,
  //                     141,
  //                     114
  //                 ]
  //             }
  //         }
  //     ]
  // }

  encode(data: DataType): Uint8Array {
    const tree: TLV.TlvNode = {
      header: {
        id: this.bundleId,
        len: 0, //  length will be replaced at the end, once we know it
        type: TLV.IdentifierType.MULTIPLE_RESOURCE,
      },
      children: [],
    };
    let totalLength = 0;
    for (const key in data) {
      const value = data[key];
      let encodedValue: Uint8Array;
      let meta = this.variables.find((m) => m.name === key);
      if (!meta) {
        meta = this.variables.find((m) => m.id === parseInt(key, 10));
      }
      // if (!meta) {
      //     // TODO use proper error with ErrorCode
      //     throw new Error(`Cannot find variable by identifier "${key}"`);
      // }
      if (meta?.converter) {
        encodedValue = meta.converter?.encode(value);
      } else {
        if (!(value instanceof Uint8Array)) {
          throw new Error(
            `Add converter for variable with identifier "${key}"`,
          );
        }
        encodedValue = value;
      }
      if (!tree.children) {
        tree.children = [];
      }
      const id = meta?.id || parseInt(key, 10);
      if (isNaN(id)) {
        throw new Error(`Cannot find variable by identifier "${key}"`);
      }
      tree.children.push({
        header: {
          id,
          len: encodedValue.length,
          type: TLV.IdentifierType.RESOURCE_VALUE,
        },
        payload: encodedValue,
      });
      totalLength += encodedValue.length + 2; // 2 for header length
    }
    tree.header.len = totalLength;
    return this.tlvConverter.encode(tree);
  }

  /**
   * Converter tlv tree to variable name => value
   */
  private _convertFromTlvTree(
    tree: TLV.TlvNode,
    variableMeta?: VariableMeta,
  ): { [key: string]: any } {
    // console.log('TLV =>', JSON.stringify(tree, null, 4));
    let result: Record<string, any> = {};
    // Recursive call to decode children
    if (tree.children) {
      result = {};
      for (const child of tree.children) {
        const variableMetas = this.variables.filter(
          (v) => v.id == child.header.id,
        );
        if (variableMetas.length == 0) {
          variableMetas.push({
            id: child.header.id,
          });
        }
        for (const variableMeta of variableMetas) {
          const key: string =
            variableMeta && variableMeta.name
              ? variableMeta.name
              : child.header.id.toString();
          result[key] = this._convertFromTlvTree(child, variableMeta);
        }
      }
    }
    // Decode current node
    else if (tree.payload) {
      const converter = variableMeta ? variableMeta.converter : undefined;
      if (converter) {
        result = converter.decode(tree.payload);
      } else {
        result = Uint8Array.from(tree.payload);
      }
    }
    return result;
  }
}
